import { Tooltip } from '@sgme/ui';
import classnames from 'classnames';
import { useToProductTypeLabel } from 'components/common-form/builder/form/node/field/ProductListField';
import type { ProductSelectorTabType } from 'components/shared/ProductAndClientSelector/productAndClientSelector.model';
import type { IntlKey } from 'locales';
import type { ProductVariation } from 'models/productsReferential';
import { FC, PropsWithChildren } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ScrollIntoViewIfNeeded from 'react-scroll-into-view-if-needed';
import type { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';
import { optionalMap } from 'utils/optional';
import { camelCaseToWords } from 'utils/string';

import { ProductPinnedButton } from './ProductPinnedButton';
import { ProductQuickStartButton } from './ProductQuickStartButton';

interface ProductListProps {
  className?: string;
  cursorAtIndex?: number;
  productVariations: readonly ProductVariation[];
  selectedProductVariation: Partial<ProductVariation>;
  onProductVariationSelected(productVariation: ProductVariation, isQuickStart: boolean, index: number): void;
  tabType: ProductSelectorTabType;
  workspace: WorkspaceWithProductSelector;
}

export const ProductList: FC<PropsWithChildren<ProductListProps>> = ({
  className,
  productVariations,
  selectedProductVariation,
  onProductVariationSelected,
  cursorAtIndex = -1,
  children,
  tabType,
  workspace,
}) => {
  const { formatMessage } = useIntl();
  const toLabel = optionalMap(useToProductTypeLabel());

  return (
    <div className={classnames('d-flex flex-column', className)}>
      <div className="overflow-auto" style={{ flexBasis: '100px', flexGrow: 1 }}>
        {productVariations.length > 0 ? (
          <ul className="list-group">
            {productVariations.map((productVariation, index) => {
              const isSelected = isEqual(productVariation, selectedProductVariation);
              return (
                <ScrollIntoViewIfNeeded
                  key={`${productVariation.productType}.${productVariation.productSubType}.${productVariation.wrapper}.${index}`}
                  className={classnames(
                    'list-group-item list-group-item-action user-select-none',
                    'd-flex justify-content-between align-items-center',
                    'border-bottom',
                    'py-2 pe-3',
                    {
                      'active border-start-0': cursorAtIndex === index,
                      'list-group-item-info active border-start-0': isSelected,
                    },
                  )}
                  active={cursorAtIndex === index}
                  onClick={() => onProductVariationSelected(productVariation, false, index)}
                  onDoubleClick={() => onProductVariationSelected(productVariation, true, index)}
                >
                  <div className="d-flex flex-column">
                    <span className={classnames(isSelected ? 'text-info' : 'text-secondary')}>
                      {toLabel(productVariation.commercialProductType) ?? (
                        <FormattedMessage
                          id={`quote.products.${productVariation.productType}` as IntlKey}
                          defaultMessage={camelCaseToWords(productVariation.productType)}
                        />
                      )}
                    </span>
                    <span className={classnames({ 'fw-semibold': isSelected })}>
                      {toLabel(productVariation.commercialProductSubType) ?? (
                        <FormattedMessage
                          id={`quote.products.${productVariation.productSubType}` as IntlKey}
                          defaultMessage={camelCaseToWords(productVariation.productSubType)}
                        />
                      )}
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    <span
                      className={classnames(
                        'badge badge-md rounded-pill mx-2 user-select-none',
                        isSelected ? 'badge-outline-info' : 'badge-outline-secondary',
                      )}
                    >
                      <FormattedMessage id={`quote.products.${productVariation.wrapper}` as IntlKey} />
                    </span>
                    {productVariation.isStatified && (
                      <Tooltip position="right" contents={formatMessage({ id: 'productSelector.dynamic.isStatified' })}>
                        <em className="icon icon-sm icon-outlined me-2">ac_unit</em>
                      </Tooltip>
                    )}
                    <ProductPinnedButton
                      className={classnames('mx-2', isSelected ? 'icon text-info' : 'icon text-muted')}
                      productVariation={productVariation}
                      tabType={tabType}
                      workspace={workspace}
                    />
                    <ProductQuickStartButton
                      onClick={() => onProductVariationSelected(productVariation, true, index)}
                      workspace={workspace}
                      tabType={tabType}
                      selectedProductVariation={productVariation}
                    />
                  </div>
                </ScrollIntoViewIfNeeded>
              );
            })}
          </ul>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

function isEqual(a: Partial<ProductVariation>, b: Partial<ProductVariation>) {
  return a.productType === b.productType && a.productSubType === b.productSubType && a.wrapper === b.wrapper;
}
